import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNotificationCounts } from "../contexts/NotificationCountsContext";
import DossierDocuments from "../components/DossierDocuments";
import CollaborateurLeavesHistory from "../components/CollaborateurLeavesHistory";
import CollaborateurAbsencesHistory from "../components/CollaborateurAbsencesHistory";
import CollaborateurPlanningHistory from "../components/CollaborateurPlanningHistory";
import MesEquipements from "../components/MesEquipements";
import MesDemandesSites from "../components/MesDemandesSites";
import MenuBadge from "../components/MenuBadge";
import StickyPageHeader from "../components/StickyPageHeader";
import RemunerationCard from "../components/RemunerationCard";
import MyRemunerationStatements from "../components/MyRemunerationStatements";
import { api } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FolderOpen, Package, CalendarRange, BedDouble, Building2, XCircle, Wallet } from "lucide-react";

const VALID_TABS = new Set(["planning", "leaves", "absences", "docs", "equip", "site-req", "remuneration"]);

export default function MonDossier() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { byPageTab } = useNotificationCounts();

  const [remunProfile, setRemunProfile] = useState(null);
  const [isSalarie, setIsSalarie] = useState(false);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [{ data: remun }, { data: cats }] = await Promise.all([
          api.get("/collaborateurs/me/remuneration"),
          api.get("/categories"),
        ]);
        if (cancelled) return;
        setRemunProfile({ ...user, ...remun });
        const cat = (cats || []).find((c) => c.id === user.category_id);
        setIsSalarie(!!cat?.is_salarie);
      } catch {
        if (!cancelled) setRemunProfile(user);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user) return null;
  const canRequestSiteEquipment = user.role === "collaborateur" || user.role === "gestionnaire";
  // Règle métier :
  //  - un collaborateur SALARIÉ ne voit PAS l'onglet « Ma rémunération »
  //    (le module ne le concerne pas — il reçoit un bulletin de salaire
  //    déposé par l'admin dans Documents / Relevé de rémunération).
  //  - un collaborateur NON-SALARIÉ garde « Ma rémunération » ; on lui
  //    cache en revanche la sous-section « Relevé de rémunération » dans
  //    Documents (rien à y consulter).
  const showRemunTab = !isSalarie;
  const requestedTab = searchParams.get("tab");
  const initialTab = (requestedTab && VALID_TABS.has(requestedTab) && (requestedTab !== "site-req" || canRequestSiteEquipment) && (requestedTab !== "remuneration" || showRemunTab))
    ? requestedTab
    : "planning";

  const badge = (key) => byPageTab[`mon-dossier.${key}`] || 0;

  return (
    <div className="space-y-6" data-testid="mon-dossier-page">
      <Tabs defaultValue={initialTab} className="w-full">
        <StickyPageHeader testid="mon-dossier-sticky-header">
        <div>
          <p className="label-uppercase mb-2">Mon profil</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
            Mon espace
          </h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            Retrouvez tous les documents qui vous concernent et le récap de vos shifts &amp; absences
          </p>
        </div>

        <div className="w-full overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="w-max h-auto">
            <TabsTrigger value="planning" data-testid="tab-planning-history">
              <CalendarRange size={14} className="mr-1.5" /> Historique de planification
              <MenuBadge count={badge("planning")} size="xs" className="ml-1.5" testid="tab-badge-mon-dossier-planning" />
            </TabsTrigger>
            <TabsTrigger value="leaves" data-testid="tab-leaves-history">
              <BedDouble size={14} className="mr-1.5" /> Historique des indisponibilités
              <MenuBadge count={badge("leaves")} size="xs" className="ml-1.5" testid="tab-badge-mon-dossier-leaves" />
            </TabsTrigger>
            <TabsTrigger value="absences" data-testid="tab-absences-history">
              <XCircle size={14} className="mr-1.5" /> Historique des absences
              <MenuBadge count={badge("absences")} size="xs" className="ml-1.5" testid="tab-badge-mon-dossier-absences" />
            </TabsTrigger>
            <TabsTrigger value="docs" data-testid="tab-docs">
              <FolderOpen size={14} className="mr-1.5" /> Documents
              <MenuBadge count={badge("docs")} size="xs" className="ml-1.5" testid="tab-badge-mon-dossier-docs" />
            </TabsTrigger>
            <TabsTrigger value="equip" data-testid="tab-equip-mine">
              <Package size={14} className="mr-1.5" /> Mes équipements &amp; matériels
              <MenuBadge count={badge("equip")} size="xs" className="ml-1.5" testid="tab-badge-mon-dossier-equip" />
            </TabsTrigger>
            {canRequestSiteEquipment && (
              <TabsTrigger value="site-req" data-testid="tab-demandes-sites">
                <Building2 size={14} className="mr-1.5" /> Demandes sites
                <MenuBadge count={badge("site-req")} size="xs" className="ml-1.5" testid="tab-badge-mon-dossier-site-req" />
              </TabsTrigger>
            )}
            {showRemunTab && (
              <TabsTrigger value="remuneration" data-testid="tab-remuneration">
                <Wallet size={14} className="mr-1.5" /> Ma rémunération
              </TabsTrigger>
            )}
          </TabsList>
        </div>
        </StickyPageHeader>

        <div className="bg-white border border-zinc-200 rounded-sm p-4 sm:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="label-uppercase mb-1">Nom</p>
              <p className="font-display text-base sm:text-lg font-semibold text-zinc-900">{user.name}</p>
            </div>
            <div>
              <p className="label-uppercase mb-1">Email</p>
              <p className="text-sm font-mono text-zinc-700 break-all">{user.email}</p>
            </div>
            <div>
              <p className="label-uppercase mb-1">Rôle</p>
              <p className="text-sm text-zinc-700">{user.role}</p>
            </div>
          </div>
        </div>

        <TabsContent value="planning">
          <CollaborateurPlanningHistory collaborateurId={user.id} />
        </TabsContent>
        <TabsContent value="leaves">
          <CollaborateurLeavesHistory collaborateurId={user.id} />
        </TabsContent>
        <TabsContent value="absences">
          <CollaborateurAbsencesHistory collaborateurId={user.id} />
        </TabsContent>
        <TabsContent value="docs">
          <DossierDocuments
            collaborateurId={user.id}
            canUpload={user.role !== "collaborateur"}
            canDelete={user.role !== "collaborateur"}
            viewerRole={user.role}
            isSalarie={isSalarie}
          />
        </TabsContent>
        <TabsContent value="equip">
          <MesEquipements />
        </TabsContent>
        {canRequestSiteEquipment && (
          <TabsContent value="site-req">
            <MesDemandesSites />
          </TabsContent>
        )}
        {showRemunTab && (
          <TabsContent value="remuneration">
            <RemunerationCard collaborateur={remunProfile || user} readOnly />
            <MyRemunerationStatements />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
